import { CloseRounded, InfoRounded, SearchRounded, StarRounded } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MenuIcon from '@mui/icons-material/Menu'
import { Accordion, AccordionDetails, AccordionSummary, Chip, Dialog, DialogContent, IconButton, InputAdornment, Paper, Stack, TextField, Typography, useTheme } from '@mui/material'
import { useReactFlow, useStoreApi, Node as XYNode } from '@xyflow/react'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import FlowApiClient from '../../../backend/sub_api_clients/FlowApiClient'
import algo from '../../../core/Algo'
import { IFlow, NodeType } from '../../../definitions/Nodes'
import { INodeDescription } from '../../../definitions/NodesDescription'
import FlowUIManager, { IFlowChange } from '../../../managers/FlowUIManager'
import NodeManager from '../../../managers/NodeManager'
import UsersManager from '../../../managers/UsersManager'
import { useAppSelector } from '../../../store/Hooks'
import { setCurrentUser } from '../../../store/slices/AppSlice'
import useCommand from '../../hooks/useCommand'
import { useDnD } from '../hooks/DnDContext'
import NodesIcon from '../nodes/NodesIcon'
import NodeCard from './NodeCard'

interface P {
    flow: IFlow
    applyFlowChange: (change: IFlowChange) => void
}

const NodesMenu = ({ flow, applyFlowChange }: P) => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const mode = useAppSelector(state => state.app.mode)
    const [, setType] = useDnD()
    const [openInfoModal, setOpenInfoModal] = useState(false)
    const [selectedNodeDescription, setSelectedNodeDescription] = useState<INodeDescription<NodeType>>(null)
    const [open, setOpen] = useState(false)
    const [loadingNodeType, setLoadingNodeType] = useState<NodeType>(null)
    const [search, setSearch] = useState('')
    const [expandedGroups, setExpandedGroups] = useState<string[]>([])
    const [userToggled, setUserToggled] = useState(false)
    const paperRef = useRef(null)
    const buttonRef = useRef(null)
    const dialogRef = useRef(null)
    const store = useStoreApi()
    const user = UsersManager.getCurrentUser()
    const { screenToFlowPosition } = useReactFlow()
    const heightNodePanel = '122px'
    const borderPanel = '2px'

    const filteredTypes = FlowUIManager.filterTypeNodes(NodeManager.getDescriptions(), flow)
        .filter(node =>
            node.name.toLowerCase().includes(search.toLowerCase())
            || (node.description && node.description.toLowerCase().includes(search.toLowerCase()))
        )

    const groups = algo.groupBy(filteredTypes, 'group')
    const sortedGroups = Array.from(groups.entries()).sort((a, b) =>
        String(a[0]).localeCompare(String(b[0]))
    )
    const sortedGroupNodes = sortedGroups.map(([groupName, nodes]) => [
        groupName,
        [...nodes].sort((a, b) => a.name.localeCompare(b.name))
    ] as [string, INodeDescription<NodeType>[]])

    const favouriteNodes = (algo.hasVal(user.favoriteNodes) && user.favoriteNodes.length > 0)
        ? NodeManager.getDescriptions()
            .filter(x => user.favoriteNodes.includes(x.type))
            .filter(node =>
                node.name.toLowerCase().includes(search.toLowerCase())
                || (node.description && node.description.toLowerCase().includes(search.toLowerCase()))
            )
            .sort((a, b) => a.name.localeCompare(b.name))
        : []

    const { execute: updateFavorite, status: updateFavoriteStatus } = useCommand({
        commandFn: (favorites: NodeType[]) => FlowApiClient.updateFavoriteNodeList(favorites, user.uuid),
        onFinish: result => {
            dispatch(setCurrentUser({ ...user, favoriteNodes: result.response }))
            setLoadingNodeType(null)
        }
    })

    const handleAddNode = async (nodeType: NodeType) => {
        const { domNode } = store.getState()
        const boundingRect = domNode?.getBoundingClientRect()

        if (boundingRect) {
            const center = screenToFlowPosition({
                x: boundingRect.x + boundingRect.width / 2,
                y: boundingRect.y + boundingRect.height / 2
            })

            const update = FlowUIManager.createNode(nodeType, center, flow)
            const newNode: XYNode = {
                id: update.newNode.uuid,
                position: update.newNode.coords,
                type: 'custom',
                data: { node: update.newNode }
            }

            const addNodes = FlowUIManager.updateNodes([{ item: newNode.data, type: 'add' }], flow)
            applyFlowChange(addNodes)
        }
    }

    const handleInfoNode = (nodeDescription: INodeDescription<NodeType>) => {
        setSelectedNodeDescription(nodeDescription)
        setOpenInfoModal(true)
    }

    const handleUpdateFavorite = async (favoriteNode: INodeDescription<NodeType>) => {
        let update: NodeType[] = []
        if (favouriteNodes.find(x => x.type === favoriteNode.type)) update = favouriteNodes.filter(x => x.type !== favoriteNode.type).map(x => x.type)
        else update = favouriteNodes.concat(favoriteNode).map(x => x.type)
        setLoadingNodeType(favoriteNode.type)
        updateFavorite(update)
    }

    const onDragStart = (event: React.DragEvent<HTMLDivElement>, nodeType: NodeType) => {
        setType(nodeType)
        event.dataTransfer.effectAllowed = 'move'
    }

    const handleTogglePaper = () => {
        setOpen(prevOpen => !prevOpen)
        if (openInfoModal) {
            setOpenInfoModal(false)
        }
    }

    const handleAccordionToggle = (groupName: string) => {
        setUserToggled(true)
        setExpandedGroups(prev =>
            prev.includes(groupName)
                ? prev.filter(g => g !== groupName)
                : [...prev, groupName]
        )
    }

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (paperRef.current && !paperRef.current.contains(e.target) && !buttonRef.current.contains(e.target) && !dialogRef.current?.contains(e.target)) {
                setOpen(false)
                setSearch('')
            }
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setOpen(false)
                setSearch('')
            }
        }

        document.addEventListener('pointerdown', handleClickOutside)
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('pointerdown', handleClickOutside)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [])

    useEffect(() => {
        if (search.trim().length < 3) {
            if (!userToggled) setExpandedGroups([])
            return
        }

        if (!userToggled) {
            const groupsWithMatches = sortedGroupNodes
                .filter(([, nodes]) => nodes.length > 0)
                .map(([groupName]) => groupName)

            if (favouriteNodes.length > 0) {
                groupsWithMatches.push('favorites')
            }

            setExpandedGroups(groupsWithMatches)
        }
    }, [search])

    useEffect(() => {
        setUserToggled(false)
    }, [search])

    const renderInfoNode = (
        <Dialog open={openInfoModal} onClose={() => setOpenInfoModal(false)} ref={dialogRef} maxWidth='xs' fullWidth>
            <DialogContent sx={{ padding: 2 }}>
                {selectedNodeDescription && (
                    <Stack gap={2}>
                        <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
                            <Stack flexDirection='row' justifyContent='center' alignItems='center' gap={1}>
                                <NodesIcon size={40} nodeType={selectedNodeDescription.type} />
                                <Typography variant='subtitle2'>
                                    {selectedNodeDescription.name}
                                </Typography>
                            </Stack>
                            <IconButton onClick={() => setOpenInfoModal(false)} size='small'>
                                <CloseRounded />
                            </IconButton>
                        </Stack>
                        <Stack gap={1}>
                            <Typography variant='subtitle2'>
                                Type:
                            </Typography>
                            <Chip
                                color='primary'
                                icon={<InfoRounded />}
                                label={selectedNodeDescription.type}
                                sx={{ width: 'fit-content' }}
                            />
                        </Stack>
                        <Stack gap={1}>
                            <Typography variant='subtitle2'>
                                Group:
                            </Typography>
                            <Chip
                                icon={<InfoRounded />}
                                label={selectedNodeDescription.group}
                                sx={{ width: 'fit-content' }}
                            />
                        </Stack>
                        <Stack gap={1}>
                            <Typography variant='subtitle2'>
                                Description:
                            </Typography>
                            <Typography variant='body2'>
                                Lorem ipsum dolor sit amet consectetur adipisicing elit. Eligendi placeat Eligendi placeat
                            </Typography>
                        </Stack>
                        <Stack gap={1}>
                            <Typography variant='subtitle2'>
                                Inputs:
                            </Typography>
                            <Stack direction='row' gap={1} flexWrap='wrap'>
                                {selectedNodeDescription.acceptedInputs.map((x, i) => (
                                    <Chip key={i} icon={<InfoRounded />} label={x.argumentKey} />
                                ))}
                            </Stack>
                        </Stack>
                        <Stack gap={1}>
                            <Typography variant='subtitle2'>
                                Accepted Inputs:
                            </Typography>
                            <Stack direction='row' gap={1} flexWrap='wrap'>
                                {selectedNodeDescription.acceptedInputNodes.map((x, i) => (
                                    <Chip key={i} icon={<InfoRounded />} label={x.nodeType} />
                                ))}
                            </Stack>
                        </Stack>
                        <Stack gap={1}>
                            <Typography variant='subtitle2'>
                                Outputs:
                            </Typography>
                            <Stack direction='row' gap={1} flexWrap='wrap'>
                                {selectedNodeDescription.outputs.map((x, i) => (
                                    <Chip key={i} icon={<InfoRounded />} label={x.key} />
                                ))}
                            </Stack>
                        </Stack>
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    )

    return (
        <>
            <IconButton ref={buttonRef} size='small' onClick={handleTogglePaper} color='primary' style={{ border: '1px solid var(--primary-color)', borderRadius: 8, backgroundColor: 'var(--bg-color)' }}>
                <MenuIcon />
            </IconButton>
            {open && (
                <Paper
                    ref={paperRef}
                    onDrop={event => {
                        event.preventDefault()
                        event.stopPropagation()
                    }}
                    sx={{
                        border: 'none',
                        padding: 'none',
                        borderRadius: 2,
                        overflowY: 'auto',
                        maxHeight: '80vh',
                        position: 'absolute',
                        top: 40
                    }}>
                    <Stack sx={{ padding: 2, paddingBottom: 1 }}>
                        <TextField
                            placeholder='Search nodes...'
                            size='small'
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <SearchRounded fontSize='small' />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ paddingBottom: 1 }}
                        />
                    </Stack>
                    <Accordion
                        disableGutters
                        disabled={sortedGroupNodes.length === 0}
                        sx={{
                            '& .MuiAccordionSummary-root:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? `rgba(255, 255, 255, 0.08)` : `rgba(0, 0, 0, 0.04)`
                            },
                            '& .MuiAccordionSummary-root.Mui-expanded': {
                                backgroundColor: 'var(--primary-selected)'
                            },
                            'border': 'none'
                        }}
                        expanded={expandedGroups.includes('favorites')}
                        onChange={() => handleAccordionToggle('favorites')}>
                        <AccordionSummary
                            expandIcon={(
                                <ExpandMoreIcon sx={{
                                    fontSize: 18,
                                    transition: 'transform 0.3s ease-in-out',
                                    transform: 'rotate(-90deg)'
                                }}
                                />
                            )}
                            sx={{
                                '.MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                                    transform: 'rotate(90deg)'
                                }
                            }}>
                            <Typography fontWeight='bold' variant='body2' textTransform='capitalize'>Favorites</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ padding: 1 }}>
                            {favouriteNodes.length === 0
                                ? (
                                        <Stack flexDirection='row' alignItems='center' justifyContent='center' gap={1} padding={2}>
                                            <Typography variant='caption'>
                                                Click on
                                            </Typography>
                                            <StarRounded fontSize='small' color='action' />
                                            <Typography variant='caption'>
                                                to add favorite nodes.
                                            </Typography>
                                        </Stack>
                                    )
                                : favouriteNodes.map(nodeDescription => (
                                    <NodeCard
                                        key={nodeDescription.type}
                                        nodeDescription={nodeDescription}
                                        mode={mode}
                                        borderPanel={borderPanel}
                                        heightNodePanel={heightNodePanel}
                                        isFavorite={true}
                                        onDragStart={onDragStart}
                                        onAddNode={handleAddNode}
                                        onInfoNode={handleInfoNode}
                                        onUpdateFavorite={handleUpdateFavorite}
                                        updateFavoriteStatus={updateFavoriteStatus}
                                        loadingNodeType={loadingNodeType}
                                    />
                                ))}
                        </AccordionDetails>
                    </Accordion>

                    {sortedGroupNodes.length === 0
                        ? (
                                <Stack flexDirection='row' alignItems='center' justifyContent='center' gap={1} padding={2} width={266}>
                                    <Typography variant='caption'>
                                        No nodes found
                                    </Typography>
                                </Stack>
                            )
                        : sortedGroupNodes.map(([groupName, nodes]) => (
                            <Accordion
                                key={groupName}
                                disableGutters
                                sx={{
                                    '& .MuiAccordionSummary-root:hover': {
                                        backgroundColor: theme.palette.mode === 'dark' ? `rgba(255, 255, 255, 0.08)` : `rgba(0, 0, 0, 0.04)`
                                    },
                                    '& .MuiAccordionSummary-root.Mui-expanded': {
                                        backgroundColor: 'var(--primary-selected)'
                                    },
                                    'border': 'none'
                                }}
                                expanded={expandedGroups.includes(groupName)}
                                onChange={() => handleAccordionToggle(groupName)}>
                                <AccordionSummary
                                    expandIcon={(
                                        <ExpandMoreIcon sx={{
                                            fontSize: 18,
                                            transition: 'transform 0.3s ease-in-out',
                                            transform: 'rotate(-90deg)'
                                        }}
                                        />
                                    )}
                                    sx={{
                                        '.MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                                            transform: 'rotate(90deg)'
                                        }
                                    }}>
                                    <Typography fontWeight='bold' variant='body2' textTransform='capitalize'>
                                        {groupName.toString().replace('-', ' ').replace('_', ' ')}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ padding: 1 }}>
                                    {nodes.map(nodeDescription => (
                                        <NodeCard
                                            key={nodeDescription.type}
                                            nodeDescription={nodeDescription}
                                            mode={mode}
                                            borderPanel={borderPanel}
                                            heightNodePanel={heightNodePanel}
                                            isFavorite={favouriteNodes.some(x => x.type === nodeDescription.type)}
                                            onDragStart={onDragStart}
                                            onAddNode={handleAddNode}
                                            onInfoNode={handleInfoNode}
                                            onUpdateFavorite={handleUpdateFavorite}
                                            updateFavoriteStatus={updateFavoriteStatus}
                                            loadingNodeType={loadingNodeType}
                                        />
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        ))}
                </Paper>
            )}
            {renderInfoNode}
        </>
    )
}

export default NodesMenu
